/**
 * Runtime settings, ARCHITECTURE.md §5.10 (`GET /api/settings`), including the
 * story presets `story.age_bands[]` (§4.6.1 columns, seeded from
 * `backend/app/story/config/age_bands.json`) and `story.voices` (role → voice,
 * seeded from `voices.json`). Secrets are never part of this shape.
 *
 * The §4.6.1 table is prose; the field names below are the schema's reading
 * of each row. Ranges are `{min, max}`; shares are fractions `0..1`; speeds
 * are Kokoro speed multipliers; times are seconds. The band's `core_words`
 * list (~1,200–4,000 words) stays in `age_bands.json` and is not exposed here.
 */
import { z } from 'zod';
import { VoiceRole } from './character';
import { NonNegInt, PosInt } from './common';
import { AgeBandId } from './vocabulary';

const IntRange = z.strictObject({ min: NonNegInt, max: NonNegInt }).refine((r) => r.min <= r.max, {
  message: 'min must not exceed max',
  path: ['max'],
});

const ShareRange = z
  .strictObject({ min: z.number().min(0).max(1), max: z.number().min(0).max(1) })
  .refine((r) => r.min <= r.max, { message: 'min must not exceed max', path: ['max'] });

export const AgeBand = z
  .strictObject({
    band: AgeBandId,
    total_words: IntRange,
    scenes: IntRange,
    words_per_scene: IntRange,
    max_words_per_sentence: PosInt,
    max_sentences_per_line: PosInt,
    /** `required` with `min_count` repetitions (≥ 3 for 3-4 / 5-6), optional for 7-8. */
    refrain: z.strictObject({ required: z.boolean(), min_count: NonNegInt }),
    dialogue_share: ShareRange,
    /** Speaking heroes. */
    max_cast: PosInt,
    max_props: NonNegInt,
    /** Wizard cards. */
    max_heroes: PosInt,
    /** Whether the wizard may add a hero beyond `max_cast` as `speaks:false`. */
    quiet_friend: z.boolean(),
    /** Stretch words allowed per scene. */
    vocab_tier: NonNegInt,
    /** `per_scene` = max questions per scene (0, 0.5 = one per two scenes, 1); `completion_at_end` adds one completion question at the end. */
    questions: z.strictObject({ per_scene: z.number().min(0), completion_at_end: z.boolean() }),
    questions_default: z.boolean(),
    subtitles_default: z.boolean(),
    /** 3-4: one call, all scenes; 5-8: one call per scene. */
    pass_b_mode: z.enum(['all_scenes', 'per_scene']),
    tts_speed: z.number().positive(),
    hold_after_line_s: z.number().min(0),
    /** Name from vocab(action) and percent-of-width per second at scale 1 (toddle 9, walk 12). */
    walk_speed: z.strictObject({ name: z.string(), pct_s: z.number().positive() }),
    /** Beat spine subset for the band (hello, wish, try_1 … goodnight). */
    beats: z.array(z.string()),
    optional_twist: z.boolean(),
  })
  .meta({ id: 'AgeBand' });
export type AgeBand = z.infer<typeof AgeBand>;

export const VoiceSetting = z
  .strictObject({
    /** Kokoro voice id (+ blends). */
    voice: z.string().min(1),
    speed: z.number().positive(),
    /** ~20-word sample sentence played by the wizard's listening check. */
    sample_text: z.string(),
  })
  .meta({ id: 'VoiceSetting' });
export type VoiceSetting = z.infer<typeof VoiceSetting>;

export const AiEffort = z.enum(['low', 'medium', 'high']);
export type AiEffort = z.infer<typeof AiEffort>;

const PerKind = <T extends z.ZodType>(v: T) => z.strictObject({ vision: v, story: v, cheap: v });

export const StoryGenerationMode = z.enum(['bones', 'spritesheet']);
export type StoryGenerationMode = z.infer<typeof StoryGenerationMode>;

export const AiSettings = z.strictObject({
  mode: z.enum(['cli', 'mock', 'agy', 'gemini', 'http', 'fallback', 'openrouter']),
  agy: z.strictObject({ model: z.string().min(1), enabled: z.boolean() }).optional(),
  gemini: z.strictObject({ model: z.string().min(1), enabled: z.boolean() }).optional(),
  openrouter: z.strictObject({ model: z.string(), writing_model: z.string().nullable(), configured: z.boolean(), enabled: z.boolean() }).optional(),
  /** Total live CLI processes (the one Limiter). */
  concurrency: PosInt,
  /** Max concurrent Pass B scene calls of one story. */
  story_fanout: PosInt,
  /** Aliases or ids. */
  models: PerKind(z.string().min(1)),
  efforts: PerKind(AiEffort),
  /** Per-call `--max-budget-usd` ceiling per kind. */
  budgets: PerKind(z.number().min(0)),
  /** 0 = off. */
  daily_budget_usd: z.number().min(0),
  /** Why the default is what it is ('subscription' → 0, 'api_key' → 25, or an explicit override). */
  daily_budget_default_reason: z.string(),
  /** `--bare` in use (iff API key). */
  bare: z.boolean(),
  /** Usage-window cooldown shared by both AI lanes, ISO text, or null. */
  paused_until: z.string().nullable(),
});
export type AiSettings = z.infer<typeof AiSettings>;

export const IndexExclusions = z
  .strictObject({
    roots: z.array(z.string()),
    dir_patterns: z.array(z.string()),
    key_patterns: z.array(z.string()),
  })
  .meta({ id: 'IndexExclusions' });
export type IndexExclusions = z.infer<typeof IndexExclusions>;

export const TtsSettings = z.strictObject({
  provider: z.enum(['kokoro', 'fish']),
  fish: z.strictObject({ voice_id: z.string(), model: z.literal('s2.1-pro-free'), configured: z.boolean() }),
});

export const Settings = z
  .strictObject({
    ai: AiSettings,
    tts: TtsSettings.optional(),
    media: z.strictObject({
      sheet_webp_mode: z.enum(['lossy', 'lossless']),
      cache_max_bytes: NonNegInt,
    }),
    index: z.strictObject({
      exclusions: IndexExclusions,
      concurrency: PosInt,
    }),
    story: z.strictObject({
      generation_mode: StoryGenerationMode.default('bones'),
      age_bands: z.array(AgeBand),
      /** Every role has a voice; the seed fills all six. */
      voices: z.record(VoiceRole, VoiceSetting),
    }),
    vocab_rev: NonNegInt,
  })
  .meta({ id: 'Settings', title: 'Settings', description: 'GET /api/settings response (secrets never included).' });
export type Settings = z.infer<typeof Settings>;

/** Body of `PATCH /api/settings`: any subset of the runtime-changeable keys (deep partial). */
export const SettingsPatch = z.strictObject({
  tts: z.strictObject({
    provider: z.enum(['kokoro', 'fish']).optional(),
    fish: z.strictObject({ voice_id: z.string().regex(/^[a-fA-F0-9]{32}$/) }).optional(),
  }).optional(),
  ai: AiSettings.partial().extend({
    mode: z.enum(['agy', 'cli', 'gemini', 'mock', 'openrouter']).optional(),
    agy: z.strictObject({ model: z.string().min(1) }).optional(),
    gemini: z.strictObject({ model: z.string().min(1) }).optional(),
    openrouter: z.strictObject({
      model: z.string().min(1).optional(), writing_model: z.string().min(1).nullable().optional(),
      api_key: z.string().min(1).optional(),
    }).optional(),
  }).optional(),
  media: Settings.shape.media.partial().optional(),
  index: Settings.shape.index.partial().optional(),
  story: z
    .strictObject({
      generation_mode: StoryGenerationMode.optional(),
      age_bands: z.array(AgeBand).optional(),
      voices: z.partialRecord(VoiceRole, VoiceSetting).optional(),
    })
    .optional(),
});
export type SettingsPatch = z.infer<typeof SettingsPatch>;
